import type { InvoiceStatus } from '@kia/shared';

/**
 * OPS-08 — the two pure display decisions behind the tax-invoice detail view.
 *
 * The write path already persists, audits, and returns a rejected invoice's INTA reasons (taxInvoice
 * repository writes errors_json; buildDto decodes it into dto.errors; GET /api/accounting/invoices/:id
 * returns it). What was missing is the READ edge: inv.list drops errors_json, so TaxInvoicePanel showed a
 * rejected row as a bare «رد شد» chip with no reason and no drill-in. The panel now opens a non-draft row,
 * fetches the already-gated GET :id, and renders dto.errors through the helpers below. Kept here (not inline)
 * so the openability rule and the defensive join are one authority and unit-tested — the repo's convention
 * (screens delegate logic to lib/*.ts).
 */

/**
 * A non-draft invoice has an outcome to inspect (a taxid, or rejection reasons); a draft has none yet and
 * instead offers the ارسال action. This is the rule for whether a row opens a detail view.
 */
export function isInspectable(status: InvoiceStatus): boolean {
  return status !== 'draft';
}

/**
 * Rejection reasons for display, defensive against a missing OR empty errors array: renders a dash rather
 * than a bare «دلیل رد: » label, and never throws on join(undefined). (`errors?.join(...) ?? '—'` would
 * NOT catch an empty array — `[].join()` is '', which `??` passes through — so it is length-gated here.)
 */
export function rejectionReasons(errors: string[] | undefined): string {
  return errors?.length ? errors.join('؛ ') : '—';
}
